/**
 * Headroom above the page's own rows while another page follows.
 *
 * <p>The position MCL's render loop reaches (see {@link mclPaging}) is bounded by the rendered
 * extent divided by its 30.8 px minimum row height — a few hundred on a 50-row page even on a
 * tall screen — so this is far outside it.
 */
export var UNKNOWN_TOTAL_HEADROOM = 10000;
/**
 * CZNA-1191 — the `totalCount` and "next" state a virtualized prev-next MCL is driven by.
 *
 * <p>MCL (`MCLRenderer.renderRows`, stripes-components 13) compares `totalCount` with a
 * **page-local** render position, while its own "next" arithmetic
 * (`pagingOffset + contentData.length !== totalCount`) wants a **global** one. The lists used to
 * satisfy the second and handed it the corpus total (or `offset + 51` above the count cap).
 * With `pagingType="prev-next"` the row loop keeps advancing that position past the last row
 * until it covers `2 × viewport + scrollTop`, and the "end of list" marker turns visible once
 * `totalCount <= renderPosition` — then it is positioned below rows that do not exist, at a
 * spot that moves with `scrollTop`. Measured: the default list (total above the cap → 51)
 * and a search for "Neruda" (58) both jumped back while scrolling and rendered 0 rows; the
 * archive (326) and review queue (101 457) never reached the marker and did not.
 *
 * <p>So `totalCount` is given in page coordinates — the rows on this page, plus
 * {@link UNKNOWN_TOTAL_HEADROOM} while another page follows so the marker stays hidden and the
 * loop never stops short — and "next" is answered explicitly (`pagingCanGoNext`), which MCL
 * prefers over its own arithmetic. It is a paging input, not a fact about the corpus: never
 * render it as a record count (CZNA-533).
 *
 * <p>A counted total that is itself out of the marker's reach is handed over unchanged, so a
 * large list keeps announcing its real size through `aria-rowcount` (MCL derives it from
 * `totalCount`); only a total small enough to reach the marker is replaced. `hasNextPage` is
 * reported by every list endpoint and wins when present — the total only answers "next" for a
 * response without it.
 */
export var mclPaging = function (_a) {
    var totalRecords = _a.totalRecords, hasNextPage = _a.hasNextPage, pageOffset = _a.pageOffset, itemCount = _a.itemCount;
    var canGoNext = hasNextPage !== null && hasNextPage !== void 0 ? hasNextPage : (totalRecords !== null && totalRecords > pageOffset + itemCount);
    if (totalRecords !== null && totalRecords >= itemCount + UNKNOWN_TOTAL_HEADROOM) {
        return { totalCount: totalRecords, canGoNext: canGoNext };
    }
    return {
        totalCount: canGoNext ? itemCount + UNKNOWN_TOTAL_HEADROOM : itemCount,
        canGoNext: canGoNext,
    };
};
